import React, { useMemo, useState } from "react";
import {
  Button,
  Title,
  Text,
  Paper,
  LoadingOverlay,
  Tabs,
  Card,
  Space
} from "@mantine/core";
import { useAuthenticatedApi, useDataProvider, useNavigation, useRouterContext } from "@hooks";
import { paymentsService } from "@services";
import Moment from "react-moment";
import { PageTitle } from "@components";
import { IconDownload } from "@tabler/icons-react";
import { IDataTableRequestModel } from "@type";
import { DynamicDataTable, IDynamicFetchParameters } from "@components";

export const Invoices = () => {
  const dataProvider = useDataProvider();
  const dataProviderInstance = useMemo(() => dataProvider(), [dataProvider]);
  const _paymentsService = useMemo(
    () => paymentsService(dataProviderInstance),
    [dataProviderInstance],
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [model, setModel] = useState<any>();
  const [activeTab, setActiveTab] = useState<string>("invoices");
  const [selectedBankPaymentId, setselectedBankPaymentId] = useState<any>();
  const [fileDownloading, setFileDownloading] = useState<boolean>(false);
  const [fileformat, setFileformat] = useState<string>();
  const [selectedReportMonth, setSelectedReportMonth] = useState<any>();
  const { push } = useNavigation();
  const { useLocation } = useRouterContext();
  const location = useLocation();

  const fetchInvoiceData = React.useCallback(
    async ({ pageSize, pageIndex, sort }: IDynamicFetchParameters) => {
      let dataTableRequest: IDataTableRequestModel = {
        PageIndex: pageIndex,
        PageSize: pageSize,
        Sort: sort.length > 0 ? sort[0].id : "",
        SortType: sort.length > 0 ? (sort[0].desc ? "desc" : "asc") : "",
      };
      const result = await _paymentsService.loadBankPayments(dataTableRequest);
      setModel(result);
      return result;
    },
    [_paymentsService]
    );

  const fetchReportData = React.useCallback(
    async ({ pageSize, pageIndex, sort }: IDynamicFetchParameters) => {
      var dataTableRequest: IDataTableRequestModel = {
        PageIndex: pageIndex,
        PageSize: pageSize,
        Sort: sort.length > 0 ? sort[0].id : "",
        SortType: sort.length > 0 ? (sort[0].desc ? "desc" : "asc") : "",
      };
      const result = await _paymentsService.loadReports(dataTableRequest);
      setModel(result);
      return result;
    },
    [_paymentsService],
  );

  useAuthenticatedApi(
    dataProviderInstance,
    () => {
      setLoading(false);
      if (location.search) {
        var val = new URLSearchParams(location.search).get("show") || "";
        setActiveTab(val);
      }
    },
    location
  );

  const viewInvoiceRemittance = (bankPaymentId: number, format: string) => {
    setselectedBankPaymentId(bankPaymentId);
    setFileDownloading(true);
    setFileformat(format);

    _paymentsService
      .viewInvoiceRemittance(bankPaymentId, format)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setFileDownloading(false);
      });
  };
  
  const viewReportRemittance = (year: string, month: string) => {
    setSelectedReportMonth(`${year}-${month}`);
    setFileDownloading(true);

    _paymentsService
      .viewReportRemittance(year, month)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setFileDownloading(false);
      });
  };

  return (
    <>
      <PageTitle
        title="Payments"
        breadcrumbs={[
          { name: "Home", route: "/dashboard" },
          { name: "Payments" },
        ]}
      />
      <Card withBorder mih={300} p="xl">
        <Title order={5} mt="md" mb="sm">
          Current Earnings: {model?.currentEarnings?.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })}
          <Text fz="xs" fw={500} component="span">
            {" "}
            (*includes all pending and un-dispatched orders that are not yet
            eligible for payment)
          </Text>
        </Title>
        <Title order={5} mt="lg" mb="sm">
          Earnings Paid (Past 15 months): 
          {" "}
          {model?.totalPaidAmount?.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })}
        </Title>
        <Tabs
          value={activeTab}
          onTabChange={(value) => push(`/payments?show=${value}`)}
        >
          <Tabs.List>
            <Tabs.Tab value="invoices">Weekly Payment</Tabs.Tab>
            <Tabs.Tab value="reports">Monthly Payment</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="invoices">
        <Paper sx={{ position: "relative" }} data-testid="payment-table">
          <LoadingOverlay visible={loading} />
          {!loading && (
            <DynamicDataTable<any, any>
              sortable={true}
              fetchData={fetchInvoiceData}
              getData={(response) => response.bankPaymentList}
              getCount={(response) => response.totalCount}
              mantineTableBodyRowProps={{}}
              columns={[
                {
                  fieldName: "bankPaymentId",
                  header: "Bank Payment ID",
                  minSize: 100,
                  sortable: false,
                  cellTemplate: (value: any) => <Text>{value}</Text>,
                },
                {
                  fieldName: "paidDate",
                  header: "Paid Date",
                  minSize: 100,
                  cellTemplate: (value: any) => (
                    <Moment format="DD/MM/YYYY">{value}</Moment>
                  ),
                },
                {
                  fieldName: "amount",
                  header: "Amount",
                  size: 100,
                  sortable: false,
                  cellTemplate: (value: any) => (
                    <Text>{value.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })}</Text>
                  ),
                },
                {
                  fieldName: "bankPaymentId",
                  header: "Download",
                  minSize: 100,
                  sortable: false,
                  cellTemplate: (value: any, row: any) => {
                    return (
                      <>
                        <Button
                          compact
                          loading={
                            fileDownloading &&
                            selectedBankPaymentId == value &&
                            fileformat == "csv"
                          }
                          leftIcon={<IconDownload size={12} />}
                          onClick={() => viewInvoiceRemittance(value, "csv")}
                          // CSV is primary by default
                          styles={{
                            root: {
                              marginRight: 20,
                            },
                          }}
                        >
                          CSV{" "}
                        </Button>
                        <Button
                          compact
                          loading={
                            fileDownloading &&
                            selectedBankPaymentId == value &&
                            fileformat == "pdf"
                          }
                          leftIcon={<IconDownload size={12} />}
                          onClick={() => viewInvoiceRemittance(value, "pdf")}
                          variant="outline"
                        >
                          PDF{" "}
                        </Button>
                      </>
                    );
                  },
                },
              ]}
            />
          )}
        </Paper>
        </Tabs.Panel>
        <Tabs.Panel value="reports">
            <LoadingOverlay visible={loading} />
            {!loading && (
              <>
                <Space h="md" />
                <DynamicDataTable<any, any>
                  sortable={true}
                  fetchData={fetchReportData}
                  getData={(response) => response.reportList}
                  getCount={(response) => response.totalCount}
                  mantineTableBodyRowProps={{}}
                          columns={[
                    {
                      fieldName: "date",
                      header: "Month",
                      minSize: 100,
                      sortable: false,
                      cellTemplate: (value: any, row: any) => <Text>{row.month} {row.year}</Text>,
                    },
                    {
                      fieldName: "fileName",
                      header: "Download",
                      minSize: 100,
                      sortable: false,
                      cellTemplate: (value: any, row: any) => {
                        return (
                            <Button
                              compact
                              loading={
                                fileDownloading &&
                                selectedReportMonth === `${row.year}-${row.month}`
                              }
                              leftIcon={<IconDownload size={12} />}
                              onClick={() =>
                                viewReportRemittance(row.year, row.month)
                              }
                            >
                              CSV{" "}
                            </Button>
                        );
                      },
                    },
                  ]}
                />
              </>
            )}
          </Tabs.Panel>
        </Tabs>
      </Card>
    </>
  );
};
